import manifest from '../../../../public/prototype/audio-manifest.json';
import {
  type DomainCommandContext,
  DomainFault,
  type DomainModule,
  type DomainReadContext,
} from '../../contracts';
import type { Command } from '../../service';
import type { Entity, Evidence } from '../../types';

export type Recording = Entity;
export type TranscriptVersion = NonNullable<Entity['versions']>[number];
export type SpeakerSegment = TranscriptVersion['segments'][number];
export type EvidenceRef = Evidence;
export interface RecordingFile {
  fileUuid: string;
  offsetMs: number;
  durationMs: number;
  url?: string;
  blob?: Blob;
}
export interface EvidenceNavigation {
  recordingUuid: string;
  transcriptVersionUuid: string;
  startMs: number;
  endMs: number;
}
export interface Bookmark {
  uuid: string;
  owner: string;
  evidence: EvidenceRef;
  note: string;
  purpose: string;
  createdAt: string;
}
export function filesFor(row: Recording): RecordingFile[] {
  if (row.audioBlob)
    return [
      {
        fileUuid: row.uuid,
        offsetMs: 0,
        durationMs:
          Number(row.fields?.durationMs) ||
          row.versions?.[0]?.segments.at(-1)?.endMs ||
          0,
        blob: row.audioBlob,
      },
    ];
  // A file manifest is optional; existing fixture identities retain their original audio.
  if (row.fields?.recordingFiles) {
    try {
      const files: RecordingFile[] = JSON.parse(row.fields.recordingFiles);
      if (
        !Array.isArray(files) ||
        files.some(
          (file) =>
            !file.fileUuid ||
            !Number.isFinite(file.offsetMs) ||
            file.offsetMs < 0 ||
            !Number.isFinite(file.durationMs) ||
            file.durationMs <= 0,
        )
      )
        return [];
      return [...files].sort((a, b) => a.offsetMs - b.offsetMs);
    } catch {
      return [];
    }
  }
  const source = row.fixture === undefined ? undefined : manifest[row.fixture];
  return source
    ? [
        {
          fileUuid: row.uuid,
          offsetMs: 0,
          durationMs: source.durationMs,
          url: source.url,
        },
      ]
    : [];
}
export function durationOf(row: Recording) {
  return Math.max(
    0,
    ...filesFor(row).map((f) => f.offsetMs + f.durationMs),
    ...(row.versions?.[0]?.segments || []).map((s) => s.endMs),
  );
}
export function recordingEndAt(row: Recording) {
  return (
    row.endAt ||
    (row.startAt
      ? new Date(Date.parse(row.startAt) + durationOf(row)).toISOString()
      : undefined)
  );
}
export function sceneOf(row: Recording) {
  const scene = row.fields?.scene;
  return (
    (
      {
        classroom: '课堂教学',
        communication: '课后沟通',
        other: '其他场景',
      } as Record<string, string>
    )[scene || ''] ||
    scene ||
    ['课堂教学', '课后沟通', '教研复盘'][row.fixture ?? -1] ||
    '未分类'
  );
}
export function locateFile(files: RecordingFile[], ms: number) {
  return files.findIndex(
    (f, i) =>
      ms >= f.offsetMs &&
      (ms < f.offsetMs + f.durationMs ||
        (i === files.length - 1 && ms === f.offsetMs + f.durationMs)),
  );
}
export function validateEvidence(
  row: Recording,
  input: EvidenceRef,
): EvidenceRef {
  const version = row.versions?.find(
    (v) => v.uuid === input.transcriptVersionUuid,
  );
  if (
    input.recordingUuid !== row.uuid ||
    !version ||
    !Number.isFinite(input.startMs) ||
    !Number.isFinite(input.endMs) ||
    input.startMs < 0 ||
    input.endMs <= input.startMs ||
    input.endMs > durationOf(row)
  )
    throw new DomainFault('VALIDATION', '片段范围或转写版本无效');
  const segments = version.segments.filter(
    (s) => s.endMs > input.startMs && s.startMs < input.endMs,
  );
  if (!segments.length)
    throw new DomainFault('VALIDATION', '所选范围没有可引用转写');
  return { ...input, quote: segments.map((s) => s.text).join('\n') };
}
export function resolveEvidence(
  ctx: Pick<DomainReadContext, 'get'>,
  input: EvidenceRef,
) {
  const row = ctx.get(input.recordingUuid);
  if (!row || row.kind !== 'recording')
    throw new DomainFault('FORBIDDEN', '来源不可访问');
  return validateEvidence(row, input);
}
export function affectedReferences(
  ctx: Pick<DomainReadContext, 'list'>,
  recordingUuid: string,
) {
  return (['clue', 'case', 'knowledge', 'followUp', 'plan'] as const)
    .flatMap((kind) => ctx.list(kind))
    .filter((row) => row.evidence?.recordingUuid === recordingUuid)
    .map((row) => ({
      uuid: row.uuid,
      kind: row.kind,
      transcriptVersionUuid: row.evidence!.transcriptVersionUuid,
    }));
}
export function evidenceUrl(e: EvidenceNavigation | EvidenceRef) {
  return `/recording/${encodeURIComponent(e.recordingUuid)}?${new URLSearchParams({ transcriptVersionUuid: e.transcriptVersionUuid, startMs: String(e.startMs), endMs: String(e.endMs) })}`;
}
export function bookmarksFor(row: Recording, owner: string): Bookmark[] {
  try {
    return (
      JSON.parse(row.fields?.segmentBookmarks || '[]') as Bookmark[]
    ).filter((b) => b.owner === owner);
  } catch {
    return [];
  }
}
export function formatTime(ms: number) {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}
export function versionDiff(old: TranscriptVersion, next: TranscriptVersion) {
  return next.segments
    .map((s, i) => ({
      before: old.segments[i]?.text || '',
      after: s.text,
      startMs: s.startMs,
    }))
    .filter((s) => s.before !== s.after);
}
function requireRecording(
  ctx: DomainCommandContext,
  uuid: string | undefined,
  kind: Entity['kind'] = 'recording',
) {
  const row = ctx.get(uuid);
  if (!row || row.kind !== kind)
    throw new DomainFault('FORBIDDEN', '来源不可访问');
  return row;
}
export function recordingCommand(
  ctx: DomainCommandContext,
  input: Command,
): Entity {
  const row = requireRecording(ctx, input.uuid);
  if (input.meta?.expectedRevision === undefined)
    throw new Error('请先预览当前版本');
  const previewAt = Number(input.fields?.previewAt);
  if (!Number.isFinite(previewAt) || Math.abs(Date.now() - previewAt) > 900000)
    throw new Error('预览已过期，请重新打开');
  switch (input.action) {
    case 'recording.bookmark': {
      const existing: Bookmark[] = JSON.parse(
        row.fields?.segmentBookmarks || '[]',
      );
      const bookmark = existing.find(
        (b) =>
          b.uuid === input.fields?.bookmarkUuid &&
          b.owner === ctx.context.viewMemberUuid,
      );
      if (input.fields?.remove === 'true') {
        if (!bookmark) throw new Error('收藏已变化，请刷新');
        row.fields = {
          ...row.fields,
          segmentBookmarks: JSON.stringify(
            existing.filter((b) => b !== bookmark),
          ),
        };
      } else {
        const evidence = validateEvidence(row, input.evidence as Evidence);
        if (bookmark) {
          bookmark.note = input.note || '';
          bookmark.purpose = input.fields?.purpose || '';
        } else
          existing.push({
            uuid: crypto.randomUUID(),
            owner: ctx.context.viewMemberUuid,
            evidence,
            note: input.note || '',
            purpose: input.fields?.purpose || '复盘',
            createdAt: ctx.now,
          });
        row.fields = {
          ...row.fields,
          segmentBookmarks: JSON.stringify(existing),
        };
      }
      break;
    }
    case 'recording.speaker': {
      const speaker = input.fields?.speaker || '';
      const role = input.fields?.speakerRole || '';
      if (
        !row.versions?.some((v) =>
          v.segments.some((segment) => segment.speaker === speaker),
        ) ||
        !['教师', '学生', '家长', '其他', '未确认'].includes(role)
      )
        throw new DomainFault('VALIDATION', '说话人或角色无效');
      row.fields = {
        ...row.fields,
        speakerRoles: JSON.stringify({
          ...JSON.parse(row.fields?.speakerRoles || '{}'),
          [speaker]: {
            role,
            confirmedAt: ctx.now,
            confirmedByMemberUuid: ctx.identity.memberUuid,
          },
        }),
      };
      break;
    }
    case 'recording.metadata':
      if (!input.title?.trim()) throw new Error('标题不能为空');
      row.title = input.title.trim();
      row.fields = {
        ...row.fields,
        tags: input.fields?.tags || '',
        archived: input.fields?.archived || row.fields?.archived || 'false',
      };
      break;
    case 'recording.associate': {
      const decision = input.fields?.decision;
      if (!['confirm', 'defer'].includes(decision || ''))
        throw new Error('关联决定无效');
      if (!input.note?.trim()) throw new Error('请填写裁决依据');
      const lesson =
        decision === 'confirm'
          ? requireRecording(ctx, input.fields?.lessonUuid, 'lesson')
          : undefined;
      if (
        lesson &&
        (lesson.campus !== row.campus ||
          (ctx.context.role === 'teacher' &&
            lesson.memberUuid !== ctx.context.viewMemberUuid))
      )
        throw new Error('课次不在允许的校区或人员范围');
      const overlaps = ctx
        .list('lesson')
        .filter(
          (e) =>
            e.campus === row.campus &&
            e.memberUuid === row.memberUuid &&
            e.startAt &&
            e.endAt &&
            row.startAt &&
            recordingEndAt(row) &&
            Date.parse(e.startAt) < Date.parse(recordingEndAt(row)!) &&
            Date.parse(e.endAt) > Date.parse(row.startAt),
        );
      if (
        lesson &&
        (overlaps.length > 1 ||
          lesson.memberUuid !== row.memberUuid ||
          (row.fields?.lessonUuid && row.fields.lessonUuid !== lesson.uuid)) &&
        input.fields?.ackConflict !== 'true'
      )
        throw new Error('存在重叠或原有关联，请明确确认冲突');
      const previous = row.fields?.lessonUuid || '';
      row.fields = {
        ...row.fields,
        linkStatus: lesson ? 'confirmed' : 'deferred',
        suggestedLessonUuid:
          row.fields?.suggestedLessonUuid || overlaps[0]?.uuid || '',
        lessonUuid: lesson?.uuid || previous,
        associationReason: input.note,
        associationHistory: JSON.stringify([
          ...JSON.parse(row.fields?.associationHistory || '[]'),
          {
            previous,
            selected: lesson?.uuid,
            decision,
            reason: input.note,
            at: ctx.now,
            actor: ctx.context.viewMemberUuid,
          },
        ]),
      };
      // Lesson counts are selectors over recording.lessonUuid; no second counter is maintained.
      if (lesson) {
        ctx.touch(lesson);
        if (previous && previous !== lesson.uuid) {
          const old = ctx.get(previous);
          if (old) ctx.touch(old);
        }
      }
      for (const task of ctx
        .list('task')
        .filter(
          (e) =>
            e.sourceUuid === row.uuid && e.fields?.taskType === 'association',
        )) {
        task.status = lesson ? 'completed' : 'pending';
        ctx.touch(task);
      }
      break;
    }
    default:
      throw new Error('未知录音命令');
  }
  ctx.touch(row);
  return row;
}

function createReference(
  ctx: DomainCommandContext,
  input: Command,
  kind: 'followUp' | 'knowledge' | 'plan',
) {
  const source = requireRecording(ctx, input.uuid);
  const opened = Number(input.fields?.previewAt);
  if (!Number.isFinite(opened) || Math.abs(Date.now() - opened) > 900000)
    throw new DomainFault('CONFLICT', '预览已过期');
  if (!input.title?.trim())
    throw new DomainFault('VALIDATION', '请填写草稿标题');
  const evidence = validateEvidence(source, input.evidence as Evidence);
  const student =
    kind === 'followUp'
      ? requireRecording(ctx, input.fields?.studentUuid, 'student')
      : undefined;
  if (student && student.campus !== source.campus)
    throw new DomainFault('VALIDATION', '请选择来源校区的学生');
  return ctx.append(kind, input.title.trim(), {
    status: 'draft',
    campus: source.campus,
    memberUuid: source.memberUuid,
    owner: ctx.context.viewMemberUuid,
    sourceUuid: source.uuid,
    evidence,
    note: input.note || '',
    fields: {
      recordingUuid: source.uuid,
      studentUuid: student?.uuid || '',
      authorView: ctx.context.viewMemberUuid,
      sourceTranscriptVersionUuid: evidence.transcriptVersionUuid,
    },
  });
}
const roles: NonNullable<DomainModule['commands'][string]>['roles'] = [
  'teacher',
  'campusManager',
  'groupOperator',
  'regionManager',
  'researchLead',
  'reviewer',
  'studentService',
];
export const recordingDomain: DomainModule = {
  namespace: 'recording',
  version: 1,
  resource: 'recording',
  pages: [
    {
      id: 'R01',
      path: '/recording',
      resource: 'recording',
      parentPath: '/recording',
      returnTo: '/recording',
    },
    {
      id: 'R02',
      path: '/recording/:recordingUuid',
      resource: 'recording',
      parentPath: '/recording',
      returnTo: '/recording',
    },
    {
      id: 'R03',
      path: '/recording/bookmark',
      resource: 'recording',
      parentPath: '/recording',
      returnTo: '/recording',
    },
    {
      id: 'R04',
      path: '/recording/pending-link',
      resource: 'recording',
      parentPath: '/recording',
      returnTo: '/recording',
    },
  ],
  migrations: [
    {
      version: 1,
      up(state) {
        state.domainData ||= {};
        state.domainData.recording ||= {};
      },
    },
  ],
  commands: {
    ...Object.fromEntries(
      ['bookmark', 'metadata', 'associate', 'speaker'].map((name) => [
        name,
        {
          roles:
            name === 'associate'
              ? ['teacher', 'campusManager', 'groupOperator', 'researchLead']
              : roles,
          kind: 'recording',
          execute(ctx: DomainCommandContext, input: Command) {
            try {
              return recordingCommand(ctx, input);
            } catch (error) {
              if (error instanceof DomainFault) throw error;
              throw new DomainFault(
                'VALIDATION',
                error instanceof Error ? error.message : '录音操作无效',
              );
            }
          },
        },
      ]),
    ),
    followUp: {
      kind: 'followUp',
      roles: ['studentService', 'campusManager', 'groupOperator'],
      execute: (ctx, input) => createReference(ctx, input, 'followUp'),
    },
    knowledge: {
      kind: 'knowledge',
      roles: ['researchLead', 'teacher', 'campusManager', 'groupOperator'],
      execute: (ctx, input) => createReference(ctx, input, 'knowledge'),
    },
    plan: {
      kind: 'plan',
      roles: ['researchLead', 'teacher', 'campusManager', 'groupOperator'],
      execute: (ctx, input) => createReference(ctx, input, 'plan'),
    },
  },
  search(ctx) {
    return ctx.list('recording').flatMap((row) =>
      (row.versions?.at(-1)?.segments || []).map((segment) => ({
        id: segment.uuid,
        entityUuid: row.uuid,
        title: row.title,
        text: segment.text,
        kind: row.kind,
        memberUuid: row.memberUuid,
        occurredAt: row.startAt || row.createdAt,
        evidence: {
          recordingUuid: row.uuid,
          transcriptVersionUuid: row.versions!.at(-1)!.uuid,
          startMs: segment.startMs,
          endMs: segment.endMs,
          quote: segment.text,
        },
      })),
    );
  },
};
